import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Divider,
  TextField,
  Grid,
  Container,
  Card,
  CardActions,
  CardHeader,
  CardContent,
} from "@mui/material";
import { useTranslations, useForm } from "hooks";
import { getTranslation } from "common";
import { isEmail, isRequired, Validation } from "hooks/useForm";
import { usersApi } from "api";
import { noSpecialChars } from "common/utilities";
import { AuthSidebar } from "components";
import { Step } from "components/LandingSidebar";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const translations = useTranslations();
  const forgotPasswordTextFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "background.paper",
    },
    "& input:-webkit-autofill": {
      WebkitBoxShadow: (theme: any) => `0 0 0 100px ${theme.palette.background.paper} inset`,
      WebkitTextFillColor: (theme: any) => theme.palette.text.primary,
      caretColor: (theme: any) => theme.palette.text.primary,
      transition: "background-color 5000s ease-in-out 0s",
    },
  };

  const validations: Validation[] = [
    ({ email }) =>
      isRequired(email) || {
        email: getTranslation(translations, "register.valid.Error_after_at"),
      },
    ({ email }) =>
      isEmail(email) || {
        email: getTranslation(translations, "register.valid.Error_after_at"),
      },
    ({ email }) =>
      noSpecialChars(email) || {
        email: getTranslation(translations, "register.valid.Error_invalid_chars"),
      },
  ];

  const { isValid, values, errors, touched, changeHandler } = useForm(
    {
      email: "",
    },
    validations
  );

  const submitData = async () => {
    const { email } = values;
    try {
      await usersApi.post("forgotpassword", {
        email,
      }, { params: { raise: false } });
    } catch (error) {
    } finally {
      navigate("/forgotpassword_confirm", { state: { email: values.email } });
    }
  };

  const steps: Step[] = [
    {
      number: 1,
      title: getTranslation(translations, "forgot.password_title1"),
      description: getTranslation(translations, "forgot.password_description1")
    },
    {
      number: 2,
      title: getTranslation(translations, "forgot.password_title2"),
      description: getTranslation(translations, "forgot.password_description2")
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        background: "background.default",
        flexDirection: { xs: "column-reverse", md: "row" },
      }}
    >
      <AuthSidebar
        title={getTranslation(translations, "2steps.password.recover")}
        steps={steps}
      />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
          background: "background.paper",
        }}
      >
        <Container
          maxWidth="sm"
          sx={{
            justifyContent: "center",
            flexDirection: "column",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Card component="form" sx={{ width: '100%', bgcolor: "background.paper" }}>
            <CardHeader title={getTranslation(translations, "forgotpassword.button.label")} />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    id="email-for-password"
                    size="small"
                    label={getTranslation(translations, "user.email.label")}
                    type="email"
                    autoComplete="off"
                    fullWidth
                    sx={forgotPasswordTextFieldSx}
                    value={values.email}
                    helperText={touched.email && errors.email}
                    error={Boolean(touched.email && errors.email)}
                    onChange={({ target }) => changeHandler("email", target.value)}
                  />
                </Grid>
              </Grid>
            </CardContent>
            <CardActions sx={{ flexDirection: 'column', gap: 2, p: 2 }}>
              <FlowentiButton
                size="small"
                variant="contained"
                type="submit"
                onClick={(e) => {
                  e.preventDefault();
                  submitData();
                }}
                id="sign-in-button"
                fullWidth
                disabled={!isValid}
              >
                {getTranslation(translations, "forgotpassword.button.label")}
              </FlowentiButton>
              <Divider sx={{ width: '100%' }} />
              <Button size="small" component={Link} to="/login" variant="text" color="inherit" fullWidth>
                {getTranslation(
                  translations,
                  "forgotpassword.link.returntologin"
                )}
              </Button>
            </CardActions>
          </Card>
        </Container>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
